import { type CardSlotUiView, pvpCardSlotViews } from '@kenocyber/game-domain';
import {
  emptyPvpCardSlots,
  formatHealthMilli,
  type PvpRuneWireKind,
  type PvpSeat,
} from '@kenocyber/shared';
import type { PvpClientState } from './connection';
import type { PvpUiState } from './machine';
import { type PvpResultText, pvpResultText } from './result-text';
import type { PvpParticipantId, PvpRenderEffects } from './view';

/**
 * The duel's interface, as one value derived from one state.
 *
 * ## Where every number comes from
 *
 * | Shown              | Source                                   |
 * | ------------------ | ---------------------------------------- |
 * | health, either ship| `state` frame, `ships[].hp`              |
 * | maximum health     | `welcome.maxHealth`                      |
 * | time remaining     | `state` frame, `left`                    |
 * | match phase        | `state` frame / `welcome`, `state`       |
 * | local seat         | `welcome.seat`                           |
 * | connection         | the client's own phase                   |
 * | result             | `ended` frame, and nothing else          |
 *
 * Every one of those is the SERVER's. Nothing here adds, subtracts, smooths or
 * predicts: a bar moves when a frame says so and at no other time, and the
 * projectiles on screen are presentation that this module cannot even see.
 *
 * ## There is no countdown, and that is a contract gap rather than a choice
 *
 * The wire has no countdown value, no authoritative start instant and no
 * deadline: `PVP_MATCH_STATES` goes `ready` -> `running` with nothing between,
 * and the welcome carries only `tickMs`, `timeLimitMs` and `reconnectGraceMs`.
 * A "3, 2, 1" derived from when React happened to receive a message would be
 * this client inventing a clock, and the first thing it would get wrong is a
 * slow connection. So the pre-match screen says it is preparing and waits for
 * the server to say `running`. The gap is recorded in the report.
 *
 * ## Nothing identifying is in here
 *
 * No ticket, no account, no match id, no generation and no ack. What reaches a
 * screen is health, time, a phase and a result.
 */

export interface PvpHealthBar {
  /** A vida corrente, em MILLI. O numerador exato da razão. */
  readonly currentMilli: bigint;
  /** O teto deste casco, em MILLI. O denominador exato da razão. */
  readonly maxMilli: bigint;
  /** Whole percent, clamped to 0-100. Never interpolated, never smoothed. */
  readonly percent: number;
  /**
   * A vida escrita, pelo formatador canónico (gate V28G.S.1).
   *
   * `100`, `108,12`, `102,001`. Até três casas, sem zeros finais e nunca em
   * notação científica - e derivada dos MESMOS milli que a percentagem, para
   * que o número e a barra não possam discordar.
   */
  readonly currentText: string;
  readonly maxText: string;
}

export type PvpConnectionTone = 'live' | 'reconnecting' | 'closed';

/**
 * One indicator, for one thing the player is carrying.
 *
 * SECONDS rather than ticks, because a player reads seconds, and rounded UP so
 * an effect with 100 ms left still says "1" - a countdown that shows zero while
 * the effect is still working is a countdown that lies about the fight.
 *
 * A charge has no seconds and says so with `null`: a teleport is held until it
 * is spent, and inventing a duration for it would be inventing a rule.
 */
export interface PvpEffectBadge {
  readonly kind: PvpRuneWireKind;
  readonly label: string;
  /** Whole seconds left, or null for something that does not expire. */
  readonly secondsLeft: number | null;
  /** How many are held. Always 1 for a timed effect. */
  readonly count: number;
}

/** What each rune is called, in the language the player is playing in. */
const EFFECT_LABEL: Readonly<Record<PvpRuneWireKind, string>> = {
  double_damage: 'Dano dobrado',
  speed: 'Velocidade',
  teleport: 'Teleporte',
};

/**
 * The badges for one seat, from the room's own counts.
 *
 * Nothing here counts down: the numbers are whatever the last state frame said,
 * so a late frame shows a stale second rather than a client-invented one, and
 * an effect the room has ended cannot linger on screen.
 */
export function pvpEffectBadges(
  effects: readonly PvpRenderEffects[],
  /*
   * A PARTICIPANT id, which in a duel is the seat.
   *
   * The same widening `view.ts` made when the renderer stopped assuming there
   * were two ships: `PvpRenderEffects.seat` has been a participant id all along,
   * and this parameter was the last place that said `'a' | 'b'`. Every value the
   * duel produces is still a legal one, and the team HUD can now ask the same
   * question about `a2` instead of needing a second copy of this function.
   */
  seat: PvpParticipantId | null,
  tickMs: number,
): PvpEffectBadge[] {
  if (seat === null) {
    return [];
  }
  const mine = effects.find((entry) => entry.seat === seat);
  if (!mine) {
    return [];
  }
  const seconds = (ticks: number) => Math.ceil((ticks * tickMs) / 1_000);
  const badges: PvpEffectBadge[] = [];
  if (mine.doubleDamageTicks > 0) {
    badges.push({
      kind: 'double_damage',
      label: EFFECT_LABEL.double_damage,
      secondsLeft: seconds(mine.doubleDamageTicks),
      count: 1,
    });
  }
  if (mine.speedTicks > 0) {
    badges.push({
      kind: 'speed',
      label: EFFECT_LABEL.speed,
      secondsLeft: seconds(mine.speedTicks),
      count: 1,
    });
  }
  /*
   * A Mirage badge was here. The rune is removed (ADR 0038), the field left the
   * wire, and there is no timer for a badge to count down.
   */
  if (mine.teleportCharges > 0) {
    badges.push({
      kind: 'teleport',
      label: EFFECT_LABEL.teleport,
      secondsLeft: null,
      count: mine.teleportCharges,
    });
  }
  return badges;
}

export interface PvpHudView {
  /** What the player is looking at, in words. */
  readonly phaseLabel: string;
  /** Present from the moment there is an arena to caption. */
  readonly you: PvpHealthBar | null;
  readonly them: PvpHealthBar | null;
  /** `m:ss` from the server's own remaining milliseconds, or null. */
  readonly timeLabel: string | null;
  readonly connection: PvpConnectionTone;
  readonly connectionLabel: string;
  readonly controlsEnabled: boolean;
  /** Shown before combat. Never a number the client made up. */
  readonly prepareLabel: string | null;
  readonly reconnecting: boolean;
  readonly result: PvpResultText | null;
  /** Said wherever the duel is on screen. */
  readonly noRewardNotice: string;
  /** What the LOCAL seat is carrying. Empty whenever there is nothing. */
  readonly effects: readonly PvpEffectBadge[];
  /**
   * **Quantas marcas de sangramento este jogador tem espalhadas** (V28G.N).
   *
   * Contadas do estado AUTORITATIVO, filtradas pela autoria, e nunca inferidas:
   * as marcas de um aliado estão sobre os mesmos alvos e não são deste jogador.
   *
   * Zero é o número que desativa o botão, e é o mesmo número em todos os ecrãs
   * onde não há duelo a decorrer - o que faz o botão nascer desativado sem que
   * nenhum ramo tenha de se lembrar disso.
   */
  readonly bleedMarks: number;
  /**
   * **Os três espaços de cartas desta cadeira** (gate V28H.J).
   *
   * Sempre três, mesmo antes de haver partida: a barra desenha três caixas, e
   * uma lista que encolhesse obrigá-la-ia a adivinhar quais faltam.
   *
   * O estado de cada um é decidido em `cards/ui.ts`, que é o mesmo modelo que a
   * caça usa - a barra não sabe o que é um arrefecimento, e este ficheiro
   * também não.
   */
  readonly cardSlots: readonly CardSlotUiView[];
  /**
   * Se o botão DETONAR pode ser premido agora.
   *
   * Três condições, e as três vêm da autoridade: há marcas, o casco está vivo, e
   * o combate está a decorrer. A morte é lida na barra de vida do próprio - a
   * mesma que o ecrã já mostra - e não numa segunda fonte que podia discordar.
   */
  readonly canDetonate: boolean;
}

const NO_REWARD = 'SEM RECOMPENSA';

/** A bar, clamped at both ends. Health can be neither negative nor above max. */
function bar(current: bigint, max: bigint): PvpHealthBar {
  const safeMax = max > 0n ? max : 0n;
  const clamped = current < 0n ? 0n : current;
  const safeCurrent = clamped > safeMax ? safeMax : clamped;
  return {
    currentMilli: safeCurrent,
    maxMilli: safeMax,
    /*
     * A RAZÃO EXATA entre os dois milli, arredondada uma vez para o desenho.
     *
     * A divisão acontece em `bigint` até ao último passo: um casco de
     * 16 000 000 milli e um de 100 000 produzem a mesma percentagem cheia, e
     * nenhum dos dois passa por um `double` que pudesse perder um dígito.
     */
    percent: safeMax === 0n ? 0 : Number((safeCurrent * 100n) / safeMax),
    currentText: formatHealthMilli(safeCurrent),
    maxText: formatHealthMilli(safeMax),
  };
}

/** `m:ss` from milliseconds. Floors, so it never shows a second that has gone. */
export function pvpTimeLabel(msLeft: number): string {
  if (!Number.isFinite(msLeft) || msLeft < 0) {
    return '0:00';
  }
  const total = Math.floor(msLeft / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function bars(
  connection: PvpClientState,
  seat: PvpSeat | null,
): { you: PvpHealthBar | null; them: PvpHealthBar | null } {
  if (seat === null || connection.ships.length === 0) {
    return { you: null, them: null };
  }
  /*
   * By SEAT, so the two clients in one duel read the same frame differently -
   * correctly. Reading by index would tell the loser they were winning.
   */
  const mine = connection.ships.find((ship) => ship.s === seat);
  const theirs = connection.ships.find((ship) => ship.s !== seat);
  /*
   * Each bar against ITS OWN hull's ceiling.
   *
   * It used to be one `arena.maxHealth` for both, which was right exactly
   * while every hull was identical - the property this protocol version stops
   * assuming. Two ships with different maxima drawn against one denominator
   * would show the tougher one as permanently wounded, or the frailer one as
   * unkillable.
   *
   * Zero when the room has not said - which is a welcome that has not landed,
   * not a hull with no health - and `bar` already renders a zero maximum as an
   * empty bar rather than dividing by it.
   */
  const ceilingOf = (participantId: string): bigint => {
    const written = connection.arena?.combatProfile[participantId]?.maxHealthMilli;
    return written === undefined ? 0n : BigInt(written);
  };
  return {
    you: mine ? bar(BigInt(mine.hp), ceilingOf(mine.s)) : null,
    them: theirs ? bar(BigInt(theirs.hp), ceilingOf(theirs.s)) : null,
  };
}

/**
 * The whole HUD, for any state the machine can be in.
 *
 * Exhaustive: the fallthrough is `never`, so a sixteenth state stops the build
 * rather than rendering whatever the nearest case happens to be.
 */
export function pvpHudView(state: PvpUiState): PvpHudView {
  const base = {
    you: null,
    them: null,
    timeLabel: null,
    connection: 'live' as PvpConnectionTone,
    connectionLabel: 'Conectado',
    controlsEnabled: false,
    prepareLabel: null,
    reconnecting: false,
    result: null,
    noRewardNotice: NO_REWARD,
    effects: [] as readonly PvpEffectBadge[],
    bleedMarks: 0,
    canDetonate: false,
    /* Três vazios: é o que se vê antes de uma partida existir. */
    cardSlots: pvpCardSlotViews(emptyPvpCardSlots(), { alive: false, inMatch: false }),
  };

  switch (state.kind) {
    case 'closed':
    /*
     * `restoring` shows exactly what `closed` shows: nothing.
     *
     * The discovery holds no match and no socket, and a HUD announcing a duel
     * this tab may turn out not to be in would be a label about a guess.
     */
    case 'restoring':
    case 'choosing':
      return { ...base, phaseLabel: 'Duelo privado' };
    case 'creating':
      return { ...base, phaseLabel: 'Criando desafio' };
    case 'waiting_for_opponent':
      return { ...base, phaseLabel: 'Aguardando adversário' };
    case 'joining':
      return { ...base, phaseLabel: 'Entrando no desafio' };
    case 'connecting':
      return { ...base, phaseLabel: 'Conectando', connectionLabel: 'Conectando…' };
    case 'entering_pvp':
      return { ...base, phaseLabel: 'Entrando na arena', prepareLabel: 'PREPARANDO DUELO' };

    case 'ready':
    case 'countdown': {
      /*
       * `countdown` is currently UNREACHABLE: the server never reports it,
       * because the wire has no countdown to report. It is handled beside
       * `ready` so that the day the contract grows one, the screen it needs
       * already exists and this comment is the only thing that has to go.
       */
      const health = bars(state.connection, state.connection.seat);
      return {
        ...base,
        // Narrowed on the kind: only `ready` carries a declaration, because
        // only `ready` is a state a player can declare from.
        phaseLabel: state.kind === 'ready' && state.declared ? 'Pronto' : 'Adversário encontrado',
        ...health,
        timeLabel: null,
        prepareLabel: 'PREPARANDO DUELO',
      };
    }

    case 'running': {
      const health = bars(state.connection, state.connection.seat);
      const seat = state.connection.seat;
      const marks =
        seat === null
          ? 0
          : state.connection.bleeding.reduce((total, mark) => total + (mark.p === seat ? 1 : 0), 0);
      return {
        ...base,
        phaseLabel: 'Em combate',
        ...health,
        // The server's own remaining milliseconds. No local clock anywhere.
        timeLabel: pvpTimeLabel(state.connection.msLeft),
        controlsEnabled: true,
        effects: pvpEffectBadges(
          state.connection.effects,
          state.connection.seat,
          state.connection.arena?.tickMs ?? 50,
        ),
        /*
         * As marcas DESTE jogador, contadas pela autoria (gate V28G.N).
         *
         * O filtro é o mesmo que a sala usa para decidir o que uma ordem rebenta,
         * portanto o número no botão e o número que detona são o mesmo número.
         */
        bleedMarks: marks,
        /*
         * Vivo, em combate, e com marcas. As três, e nenhuma delas é local: a
         * vida vem da barra que a sala publicou, e a fase vem do estado dela.
         */
        canDetonate: marks > 0 && (health.you?.currentMilli ?? 0n) > 0n,
        /*
         * Os três espaços, com a vida a decidir o bloqueio (gate V28H.J).
         *
         * Uma nave abatida bloqueia o que ainda não está a correr, e não
         * interrompe o que já está - a decisão é do modelo, e a vida que ele lê
         * é a que a sala publicou.
         */
        cardSlots: pvpCardSlotViews(state.connection.cards, {
          alive: (health.you?.currentMilli ?? 0n) > 0n,
          inMatch: true,
        }),
      };
    }

    case 'reconnecting': {
      const health = bars(state.connection, state.connection.seat);
      return {
        ...base,
        /*
         * Not "Reconectando" - the alert beside it already says that, and the
         * screenshot showed the word three times in one column. This says the
         * other true thing: what is on screen is the last frame the server
         * confirmed, and nothing has moved since.
         */
        phaseLabel: 'Último estado confirmado',
        ...health,
        /*
         * The last authoritative time, frozen. It does not tick down while the
         * socket is away: the client has no authority to advance it, and a
         * clock running through a disconnection is a clock that lies.
         */
        timeLabel: pvpTimeLabel(state.connection.msLeft),
        connection: 'reconnecting',
        connectionLabel: 'Reconectando…',
        reconnecting: true,
      };
    }

    case 'completed':
      return {
        ...base,
        phaseLabel: 'Duelo encerrado',
        connection: 'closed',
        connectionLabel: 'Encerrado',
        // The only road to a result, straight from the exhaustive mapping.
        result: pvpResultText(state.result, state.match.seat),
      };

    case 'leaving':
      return { ...base, phaseLabel: 'Encerrando', connection: 'closed', connectionLabel: '—' };

    case 'recovering_hunt':
      return {
        ...base,
        phaseLabel: 'Retornando ao jogo',
        connection: 'closed',
        connectionLabel: '—',
      };

    case 'error':
      return {
        ...base,
        phaseLabel: 'Duelo interrompido',
        connection: 'closed',
        connectionLabel: '—',
      };

    default: {
      // A sixteenth state stops the build here rather than rendering something
      // plausible for a case nobody thought about.
      const unhandled: never = state;
      throw new Error(`unhandled pvp ui state: ${JSON.stringify(unhandled)}`);
    }
  }
}

/**
 * How long the room holds a dropped seat, if the welcome said.
 *
 * A DURATION, not a live countdown. The client knows the window's length but
 * not when its own drop happened - `PvpConnection` measures that against its
 * injected clock and does not publish it - so a ticking "8s left" would be this
 * module inventing a deadline. Recorded as a gap rather than guessed at.
 */
export function pvpGraceSeconds(connection: PvpClientState): number | null {
  const grace = connection.arena?.reconnectGraceMs;
  return typeof grace === 'number' && grace > 0 ? Math.round(grace / 1000) : null;
}
